import {
  EmbindVector,
  PRFileMarina,
  PRMarinaNode,
  PRRect,
} from '@sketch-hq/sketch-web-renderer'
import { SketchElementNode } from '../types'
import { createSketchElementNode } from './createSketchElementNode'

/**
 * Context to share information and access it from any point of the recursion.
 */
type BuildSchetchSceneContext = {
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
}

/**
 * Create the tree of layers to match the one in the Sketch app based on the prFile.
 */
export function buildSketchSceneTreeFromPRFile(
  prFile: PRFileMarina
): SketchElementNode | null {
  const prFileMarinaRootNode = prFile.getRootNode()
  if (!prFileMarinaRootNode) {
    return null
  }

  /** The bounds of the artboard root. */
  const rootNodeBounds = prFileMarinaRootNode.getAbsoluteBounds()

  // We use a an object so we can mutate the value
  // and access it from anywhere in the tree to keep track
  // of the index that the next inserted node should use.
  const nodeIndexState = {
    lastNodeIndex: null,
  }
  const layerRootNode = getSketchElementNodeListToUseAsChildren(
    prFileMarinaRootNode,
    nodeIndexState,
    {
      rootNodeBounds,
    }
  )[0]

  return layerRootNode ?? null
}

/**
 * Function recursively rebuilding the tree of layers like in the Sketch app.
 * Note: The tree of marina node is a mostly used for rendering and contains extra nodes
 * that are not part of the layers created by the designer and therefore we don't want to show
 * in the Sketch elements tree.
 *
 * This function recursively goes through nodes and remove the nodes that are not layers
 * and flatten the structure of the mask nodes when needed.
 */
function getSketchElementNodeListToUseAsChildren(
  currentMarinaNode: PRMarinaNode,
  nodeIndexState: { lastNodeIndex: number | null },
  context: BuildSchetchSceneContext
) {
  // We want the artboard to have index -1. The first layer should have index 0.
  const nextNodeIndex =
    nodeIndexState.lastNodeIndex === null
      ? -1
      : nodeIndexState.lastNodeIndex + 1

  const currentSketchElementNode = currentMarinaNode.isLayer()
    ? createSketchElementNode(
        currentMarinaNode,
        nextNodeIndex,
        context.rootNodeBounds
      )
    : null

  const shouldKeepNode =
    // Older versions of Sketch can create empty groups. We don't want them in the inspector as
    // the inspector json data does not contain them either. Keeping them would create a mismatch
    // between the "layerRenderingIndex" and the "order" property of the layers in the inspector data,
    // which would not allow us to link the two correctly.
    Boolean(currentSketchElementNode) && !isEmptyGroup(currentMarinaNode)

  if (shouldKeepNode) {
    // Mutate the state object, so that other functions called recursively when processing the descendants
    // can always access the latest value.
    nodeIndexState.lastNodeIndex = nextNodeIndex
  }

  // List of sub nodes we still need to visit
  const prMarinaNodeChildrenVector = currentMarinaNode.getChildren()

  // This is the list of Sketch element nodes derived from prMarinaNodeChildrenVector.
  // i.e. the list of sub layers under currentMarinaNode
  let sketchElementNodesToUseAsChildren: SketchElementNode[] = []

  for (let i = 0; i < prMarinaNodeChildrenVector.size(); i++) {
    const childPrMarinaNode = prMarinaNodeChildrenVector.get(i)
    if (childPrMarinaNode) {
      // If we are were not skipping node layersListForChild would always be one node. But because,
      // we can remove nodes, we can inherit multiple children from the sub nodes.
      const layersListForChild = getSketchElementNodeListToUseAsChildren(
        childPrMarinaNode,
        nodeIndexState,
        context
      )

      // Insert before to get the list in reverse order.
      // The children are reversed because the vector contains the layers in the rendering order,
      // so bottom layer is first. When showing the tree of layers we want to show top layer first
      // like in Sketch app.
      sketchElementNodesToUseAsChildren = [
        ...layersListForChild,
        ...sketchElementNodesToUseAsChildren,
      ]
    }
  }

  if (!shouldKeepNode) {
    // We are skipping the current node that was supposed to be used as a child but
    // we want to keep the potential layers in the sub nodes and attach
    // them directly to the parent instead of to the the current node.
    return sketchElementNodesToUseAsChildren
  }

  // Typescript can't resolve on it's own that currentSketchElementNode is defined if we have shouldKeepNode true
  const nonNullableCurrentSketchElementNode = currentSketchElementNode as SketchElementNode

  if (isMaskLayerToFlatten(currentMarinaNode)) {
    /** Flatten the structure of the mask, @see isMaskLayerToFlatten for more details */
    // "Maskee" node children that are layers become siblings of the mask layer
    return [
      ...sketchElementNodesToUseAsChildren,
      nonNullableCurrentSketchElementNode,
    ]
  }

  nonNullableCurrentSketchElementNode.children = sketchElementNodesToUseAsChildren

  return [nonNullableCurrentSketchElementNode]
}

/**
 * When a layer was converted to mask, the marina scene node structure can change based
 * on the style of the layer (if layer has border or not). The "Maskees" node containing
 * the nodes of the layers affected by the mask can change position.
 *
 * - In the case where the mask has border, the Maskee node is just a sibling of the mask layer.
 * - In the case where the layer does not have borders, the mask layer becomes the parent of a "Clip" layer which has for children the layers affected by the mask.
 *
 * The latter structure needs to be flatten when representing the layers tree so it is presented
 * like in the Sketch app.
 *
 * With borders (already flattened):
 * Parent
 * |__MyMask
 * |____...
 * |____Clip
 * |__Maskees
 * |____LayerAffectedByMask
 *
 * Without borders (needs to be flattened)):
 * Parent
 * |__MyMask
 * |____...
 * |____Clip
 * |______Maskees
 * |________LayerAffectedByMask
 *
 */
function isMaskLayerToFlatten(prMarinaNode: PRMarinaNode) {
  const clipNodeChild = findInChildren(prMarinaNode, isClipNode)

  if (clipNodeChild) {
    const maskeesNode = findInChildren(clipNodeChild, isMaskeesNode)
    if (maskeesNode) {
      return true
    }
  }

  return false
}

/** Check if the node is the special "Maskees" node. */
function isMaskeesNode(prMarinaNode: PRMarinaNode) {
  return prMarinaNode.getName() === 'Maskees' && !prMarinaNode.isLayer()
}

/** Check if the node is the special "Clip" node. */
function isClipNode(prMarinaNode: PRMarinaNode) {
  return prMarinaNode.getName() === 'Clip' && !prMarinaNode.isLayer()
}

/**
 * Find the first child that matches the predicate
 */
function findInChildren(
  prMarinaNode: PRMarinaNode,
  predicate: (prMarinaNode: PRMarinaNode) => boolean
) {
  const prMarinaNodeChildrenVector = prMarinaNode.getChildren()

  for (let i = 0; i < prMarinaNodeChildrenVector.size(); i++) {
    const childPrMarinaNode = prMarinaNodeChildrenVector.get(i)
    if (childPrMarinaNode && predicate(childPrMarinaNode)) {
      return childPrMarinaNode
    }
  }
}

/**
 * An empty group could be a group with no children or with only children that are not layers.
 */
function isEmptyGroup(prMarinaNode: PRMarinaNode) {
  // Artboards are also marked as group.
  const isGroupLayer = prMarinaNode.isGroup() && !prMarinaNode.isArtboard()
  const isEmpty = !hasAnyLayerInChildren(prMarinaNode.getChildren())

  if (isGroupLayer && isEmpty) {
    return true
  }

  return false
}

/**
 * We were initially just checking prMarinaNode.getChildren().size() === 0 to know
 * if a group was empty. But we found out that in the PR file, some groups can have "hidden" children
 * when a group just has layers that are not visible.
 *
 * The goal is to match the output of the Inspector json file, so we need to be careful:
 * For example we thought that we would need to handle the case of an empty group
 * inside an empty group. But it turns out that the Inspector json file does not handle that case
 * so handling it here would create discrepancies we don't want.
 */
function hasAnyLayerInChildren(childrenVector: EmbindVector<PRMarinaNode>) {
  for (let i = 0; i < childrenVector.size(); i++) {
    const child = childrenVector.get(i)
    if (child?.isLayer()) {
      return true
    }

    // Here we are not sure if we need to check the descendants of the children as well
    // in case a child is not a layer but does contain a layer. If this case arises one day,
    // we can just call hasAnyLayerInDescendent(child.getChildren()) recursively.
  }

  return false
}
